import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FolderKanban, Cpu, Calendar, Users } from "lucide-react";
import Header from "../components/layout/Header";
import { StatCard, Badge, SearchInput, Button, Table } from "../components/ui/index";
import { projetsAPI, cascadeAPI } from "../api/services";
import { formatDateMedium } from "../utils/dateUtils";
import { useAuth } from "../context/AuthContext";
import AddProjetModal from "../components/modals/AddProjetModal";
import EditProjetModal from "../components/modals/EditProjetModal";
import ConfirmDeleteModal from "../components/modals/ConfirmDeleteModal";
import ActionMenu from "../components/ui/ActionMenu";

export default function Projets() {
  const navigate = useNavigate();
  const { user, canDo, adminMode } = useAuth();
  const { t } = useTranslation();
  const isRevendeur = user?.role === 'revendeur';
  const isMembre = user?.role === 'membre';
  const isClient = user?.role === 'client';
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [filterRevendeur, setFilterRevendeur] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [projets, setProjets] = useState([]);
  const [revendeurs, setRevendeurs] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenu, setOpenMenu] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [cardFilter, setCardFilter] = useState("all");

  useEffect(() => {
    if (isClient) return;
    cascadeAPI.revendeurs().then(res => setRevendeurs(res.data.data)).catch(() => {});
  }, [isClient]);

  useEffect(() => {
    if (isClient) return;
    if (filterRevendeur) {
      cascadeAPI.clients(filterRevendeur).then(res => setClientsList(res.data.data)).catch(() => {});
    } else {
      cascadeAPI.clients().then(res => setClientsList(res.data.data)).catch(() => {});
    }
    setFilterClient("");
  }, [filterRevendeur, isClient]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = { search };
        if (filterRevendeur) params.revendeur = filterRevendeur;
        if (filterClient) params.client = filterClient;
        const res = await projetsAPI.getAll(params);
        let data = res.data.data;
        // Client ne voit que les projets terminés
        if (isClient) data = data.filter(p => p.etat === 'Terminé');
        setProjets(data);
      } catch (err) {
        console.error("Erreur chargement projets:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [search, filterRevendeur, filterClient]);

  const handleAdd = async (p) => {
    try {
      await projetsAPI.create(p);
      setShowModal(false);
      const res = await projetsAPI.getAll();
      setProjets(res.data.data);
    } catch (err) {
      console.error("Erreur création:", err);
    }
  };

  const handleEdit = async (data) => {
    await projetsAPI.update(editItem._id, data);
    setEditItem(null);
    const res = await projetsAPI.getAll();
    setProjets(res.data.data);
  };

  const handleDelete = async () => {
    try {
      await projetsAPI.remove(deleteItem._id);
      setDeleteItem(null);
      const res = await projetsAPI.getAll();
      setProjets(res.data.data);
    } catch (err) {
      console.error("Erreur suppression:", err);
    }
  };

  const totalAppareils = projets.reduce((s, p) => s + (p.appareils || 0), 0);
  const actifs = projets.filter(p => p.etat === "Actif").length;
  const enCreation = projets.filter(p => p.etat === "En cours de création").length;

  const filteredProjets = cardFilter === "all" ? projets
    : cardFilter === "actif" ? projets.filter(p => p.etat === "Actif")
    : cardFilter === "creation" ? projets.filter(p => p.etat === "En cours de création")
    : projets;

  const toggleCard = (key) => setCardFilter(prev => prev === key ? "all" : key);

  return (
    <>
      <Header title={t('projects.title')} subtitle={`${projets.length} ${t('projects.projectsCount')}`} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
        <StatCard icon={FolderKanban} iconBg="bg-blue-100 text-blue-600" label={t('projects.totalProjects')} value={projets.length} onClick={() => setCardFilter("all")} active={cardFilter === "all"} />
        <StatCard icon={FolderKanban} iconBg="bg-green-100 text-green-600" label={t('projects.activeProjects')} value={actifs} onClick={() => toggleCard("actif")} active={cardFilter === "actif"} />
        <StatCard icon={Calendar} iconBg="bg-orange-100 text-orange-600" label={t('projects.inCreation')} value={enCreation} onClick={() => toggleCard("creation")} active={cardFilter === "creation"} />
        <StatCard icon={Cpu} iconBg="bg-purple-100 text-purple-600" label={t('projects.totalDevices')} value={totalAppareils} onClick={() => navigate('/noeuds')} />
        {canDo('creerProjet') && (
          <Button onClick={() => setShowModal(true)} className="h-full justify-center gap-2">
            <FolderKanban size={18} /> {t('projects.addProject')}
          </Button>
        )}
      </div>

      {!isMembre && !isClient && (
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
          {adminMode === 'platform' && (
            <select value={filterRevendeur} onChange={e => setFilterRevendeur(e.target.value)}
              className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">{t('common.allResellers')} ({revendeurs.length})</option>
              {revendeurs.map(r => <option key={r._id} value={r._id}>{r.nom}</option>)}
            </select>
          )}
          <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
            className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">{t('common.allClients')} ({clientsList.length})</option>
            {clientsList.map(c => <option key={c._id} value={c._id}>{c.nom}</option>)}
          </select>
        </div>
      )}

      <div className="mb-4">
        <SearchInput value={search} onChange={setSearch} />
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">{t('common.loading')}</div>
      ) : (
        <Table headers={[t('projects.colProject'), t('projects.colResellerClient'), t('projects.colTeam'), t('projects.colDevices'), t('projects.colStartDate'), t('projects.colState'), t('projects.colActions')]}>
          {filteredProjets.map((p) => (
            <tr key={p._id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => navigate(`/projets/${p._id}`)}>
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                    <FolderKanban size={18} className="text-blue-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{p.nom}</p>
                    <p className="text-xs text-slate-400">ID: {p._id?.slice(-6)}</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <p className="text-sm font-medium text-slate-800">{p.revendeur?.nom || "—"}</p>
                <p className="text-xs text-slate-400">{p.client?.nom || "—"}</p>
              </td>
              <td className="px-6 py-4">
                <div className="space-y-1">
                  {(p.chefDeProjet || []).map((m, i) => (
                    <div key={`cp-${i}`} className="flex items-center gap-1.5 text-xs text-blue-600">
                      <Users size={11} className="text-blue-400" /> {m.nom || m}
                    </div>
                  ))}
                  {(p.responsableTechnique || []).map((m, i) => (
                    <div key={`rt-${i}`} className="flex items-center gap-1.5 text-xs text-orange-600">
                      <Users size={11} className="text-orange-400" /> {m.nom || m}
                    </div>
                  ))}
                  {(!p.chefDeProjet?.length && !p.responsableTechnique?.length) && <span className="text-xs text-slate-400">—</span>}
                </div>
              </td>
              <td className="px-6 py-4">
                <p className="text-sm font-semibold text-slate-800">{p.appareils || 0} {t('clients.total')}</p>
                <p className="text-xs text-green-600 font-medium">{p.appareilsActifs || 0} {t('clients.active')}</p>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-1.5 text-sm text-slate-600">
                  <Calendar size={13} className="text-slate-400" />
                  {formatDateMedium(p.dateDebut)}
                </div>
              </td>
              <td className="px-6 py-4"><Badge label={p.etat} /></td>
              <td className="px-6 py-4" onClick={e => e.stopPropagation()}>
                <ActionMenu rowId={p._id} openMenu={openMenu} setOpenMenu={setOpenMenu} onEdit={() => setEditItem(p)} onDelete={() => setDeleteItem(p)} />
              </td>
            </tr>
          ))}
        </Table>
      )}

      <AddProjetModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onAdd={handleAdd}
      />

      <EditProjetModal
        isOpen={!!editItem}
        onClose={() => setEditItem(null)}
        onEdit={handleEdit}
        item={editItem}
      />

      <ConfirmDeleteModal
        isOpen={!!deleteItem}
        onClose={() => setDeleteItem(null)}
        onConfirm={handleDelete}
        itemName={deleteItem?.nom}
        itemType="projet"
      />
    </>
  );
}
